/**
 * The settings panel: sound, how shop items show, the yard's water and
 * background motion, export, import, and wipe.
 * There is no Save button: the game saves every 30 s, on leaving, and after
 * anything worth keeping (see saveNow in main.ts). It never
 * touches game state directly; each control calls back into the composition
 * root, which owns the state and the save store.
 */

import type { Sound } from './sound';
import { itemViewIsRows, setItemViewRows } from './itemView';
import { backgroundScrolls, setMotion, waterMoves } from './motion';

export interface SettingsHooks {
  sound: Sound;
  /** Persist immediately. */
  /** The current save as an export string. */
  exportSave(): string;
  /** Replace the game with a pasted save. Returns false if it could not be read. */
  importSave(encoded: string): boolean;
  /** Erase everything and start over. */
  wipe(): void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function button(label: string, className = ''): HTMLButtonElement {
  const b = el('button', `settings__button ${className}`.trim(), label);
  b.type = 'button';
  return b;
}

/** Steps of the volume slider, so it lands on round numbers. */
const VOLUME_STEP = 5;

/**
 * The speaker, as rects on a 10 by 8 grid. Drawn rather than rendered: at
 * this size there is nothing for a sprite to say that four rectangles cannot,
 * and it takes its colour from the button so it dims and lights with it.
 */
const SPEAKER: readonly Rect[] = [
  [1, 3, 2, 2],
  [3, 2, 1, 4],
  [4, 1, 1, 6],
];
/** Two arcs leaving the cone, the nearer one shorter. */
const WAVES: readonly Rect[] = [
  [6, 3, 1, 2],
  [8, 2, 1, 4],
];
/** A cross where the waves would be, for muted. */
const CROSS: readonly Rect[] = [
  [6, 2, 1, 1],
  [7, 3, 1, 1],
  [8, 4, 1, 1],
  [9, 5, 1, 1],
  [9, 2, 1, 1],
  [8, 3, 1, 1],
  [7, 4, 1, 1],
  [6, 5, 1, 1],
];

type Rect = readonly [number, number, number, number];

const SVG_NS = 'http://www.w3.org/2000/svg';

function speakerIcon(muted: boolean): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 10 8');
  svg.setAttribute('width', '20');
  svg.setAttribute('height', '16');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('shape-rendering', 'crispEdges');
  for (const [x, y, w, h] of [...SPEAKER, ...(muted ? CROSS : WAVES)]) {
    const rect = document.createElementNS(SVG_NS, 'rect');
    rect.setAttribute('x', String(x));
    rect.setAttribute('y', String(y));
    rect.setAttribute('width', String(w));
    rect.setAttribute('height', String(h));
    rect.setAttribute('fill', 'currentColor');
    svg.append(rect);
  }
  return svg;
}

/**
 * The volume control: a slider and the figure it is at. The sample plays on
 * release rather than on every input event, or dragging would machine-gun the
 * click sound all the way across the track.
 */
function volumeControl(sound: Sound): {
  mute: HTMLButtonElement;
  slider: HTMLInputElement;
  readout: HTMLElement;
} {
  const slider = document.createElement('input');
  slider.type = 'range';
  slider.className = 'settings__slider';
  slider.min = '0';
  slider.max = '100';
  slider.step = String(VOLUME_STEP);
  slider.value = String(Math.round(sound.volume * 100));
  slider.setAttribute('aria-label', 'Volume');

  const mute = el('button', 'settings__icon');
  mute.type = 'button';

  const readout = el('span', 'settings__volume');
  const show = (): void => {
    const pct = Number(slider.value);
    const off = sound.muted || pct === 0;
    readout.textContent = off ? 'off' : `${pct}%`;
    slider.setAttribute('aria-valuetext', readout.textContent);
    slider.classList.toggle('settings__slider--muted', sound.muted);
    mute.replaceChildren(speakerIcon(off));
    mute.setAttribute('aria-pressed', String(sound.muted));
    mute.setAttribute('aria-label', sound.muted ? 'Unmute' : 'Mute');
    mute.title = sound.muted ? 'Unmute' : 'Mute';
  };
  show();

  slider.addEventListener('input', () => {
    sound.setVolume(Number(slider.value) / 100);
    // Reaching for the slider means wanting to hear it, so it comes off mute
    // rather than moving a control that does nothing.
    if (sound.muted && Number(slider.value) > 0) sound.setMuted(false);
    show();
  });
  // A sample at the level just chosen, so the number means something.
  slider.addEventListener('change', () => sound.play('click'));

  mute.addEventListener('click', () => {
    sound.setMuted(!sound.muted);
    show();
    if (!sound.muted) sound.play('click');
  });

  return { mute, slider, readout };
}

/** Builds the settings panel body; the caller decides where it opens from. */
export function createSettingsView(hooks: SettingsHooks): HTMLElement {
  const status = el('p', 'settings__status', '');
  let statusTimer = 0;
  function flash(text: string, isError = false): void {
    status.textContent = text;
    status.classList.toggle('settings__status--error', isError);
    window.clearTimeout(statusTimer);
    statusTimer = window.setTimeout(() => {
      status.textContent = '';
    }, 3000);
  }

  // Sound
  const volume = volumeControl(hooks.sound);

  // Shop items: icons, with the details in a tooltip, or rows that spell
  // them out (ui/itemView.ts). Rows by default on a touch screen.
  const iconsBtn = button('Icons', 'settings__choice');
  const rowsBtn = button('Rows', 'settings__choice');
  const showView = (): void => {
    const rows = itemViewIsRows();
    iconsBtn.setAttribute('aria-pressed', String(!rows));
    rowsBtn.setAttribute('aria-pressed', String(rows));
  };
  iconsBtn.addEventListener('click', () => {
    setItemViewRows(false);
    showView();
  });
  rowsBtn.addEventListener('click', () => {
    setItemViewRows(true);
    showView();
  });
  showView();

  // The yard's ambient motion (ui/motion.ts): the water washing up the shore
  // and the background scrolling past, each on or held still.
  const motionChoice = (
    which: 'water' | 'scroll',
    isOn: () => boolean,
    onLabel: string,
  ): HTMLButtonElement[] => {
    const onBtn = button(onLabel, 'settings__choice');
    const offBtn = button('Still', 'settings__choice');
    const show = (): void => {
      onBtn.setAttribute('aria-pressed', String(isOn()));
      offBtn.setAttribute('aria-pressed', String(!isOn()));
    };
    onBtn.addEventListener('click', () => {
      setMotion(which, true);
      show();
    });
    offBtn.addEventListener('click', () => {
      setMotion(which, false);
      show();
    });
    show();
    return [onBtn, offBtn];
  };
  const waterBtns = motionChoice('water', waterMoves, 'Waves');
  const scrollBtns = motionChoice('scroll', backgroundScrolls, 'Scroll');

  // Export
  const exportArea = el('textarea', 'settings__area');
  exportArea.readOnly = true;
  exportArea.rows = 3;
  exportArea.placeholder = 'Your save will appear here.';
  const exportBtn = button('Export');
  exportBtn.addEventListener('click', () => {
    exportArea.value = hooks.exportSave();
    exportArea.select();
    flash('Export ready. Copy the text above.');
  });
  const copyBtn = button('Copy');
  copyBtn.addEventListener('click', async () => {
    // Always the save as it is now: an export made earlier is out of date.
    exportArea.value = hooks.exportSave();
    try {
      await navigator.clipboard.writeText(exportArea.value);
      flash('Copied to clipboard.');
    } catch {
      exportArea.select();
      flash('Select the text above and copy it.', true);
    }
  });

  // Import
  const importArea = el('textarea', 'settings__area');
  importArea.rows = 3;
  importArea.placeholder = 'Paste a save here.';
  const importBtn = button('Import');
  importBtn.addEventListener('click', () => {
    const text = importArea.value.trim();
    if (!text) {
      flash('Paste a save first.', true);
      return;
    }
    if (!window.confirm('Importing replaces your current game. Continue?')) return;
    if (hooks.importSave(text)) {
      importArea.value = '';
      flash('Save imported.');
    } else {
      flash('That is not a valid save.', true);
    }
  });

  // Wipe
  const wipeBtn = button('Wipe save', 'settings__button--danger');
  wipeBtn.addEventListener('click', () => {
    if (!window.confirm('Erase your save and start over? This cannot be undone.')) return;
    if (!window.confirm('Really erase everything?')) return;
    hooks.wipe();
    flash('Save wiped.');
  });

  const body = el('div', 'settings__body');
  const row = (label: string, ...controls: HTMLElement[]): HTMLElement => {
    const r = el('div', 'settings__row');
    r.append(el('span', 'settings__label', label), ...controls);
    return r;
  };
  body.append(
    row('Sound', volume.mute, volume.slider, volume.readout),
    row('Shop items', iconsBtn, rowsBtn),
    row('Water', ...waterBtns),
    row('Background', ...scrollBtns),
    row('Export', exportBtn, copyBtn),
    exportArea,
    row('Import', importBtn),
    importArea,
    row('Danger', wipeBtn),
    status,
  );
  return body;
}
